import React, { useState, useEffect, useCallback } from 'react';
import { Fade, TableRow, TableCell, alpha, Typography } from '@mui/material';
import { ShoppingCart as Icon } from '@mui/icons-material';
import ReportLayout, { ReportColumn, SummaryCard, fmt2, fmtDate } from '../../components/reports/ReportLayout';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, PurchaseRow } from '../../services/reportService';
import { tradesService } from '../../services/tradesService';
import { AccountBalance as CostIcon } from '@mui/icons-material';
import { CostRow } from '../../services/reportService';
 
const COST_ACCENT = '#9C27B0';
 
const CostInfoReport: React.FC = () => {
  const [rows, setRows]     = useState<CostRow[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [costCats, setCostCats] = useState<any[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({
    date_from: '', date_to: '', cost_category_id: '',
  });
 
  useEffect(() => {
    tradesService.listCostCategories({ is_active: true })
      .then(r => setCostCats(r.results.map((c: any) => ({ value: c.cost_category_id, label: c.cost_category_name }))))
      .catch(() => {});
  }, []);
 
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
      const res = await reportService.getCostReport(p);
      setRows(res.results); setTotals((res.totals || {}) as any);
    } catch {} finally { setLoading(false); }
  }, [filters]);
 
  useEffect(() => { fetchData(); }, [fetchData]);
 
  const handleDownload = (fmt: 'excel' | 'pdf') => {
    const p: any = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return reportService.downloadCost(fmt, p);
  };
 
  const columns: ReportColumn[] = [
    { key: 'cost_date',          label: 'Date',         format: v => fmtDate(v), noWrap: true },
    { key: 'cost_category_name', label: 'Category',     format: v => v || '—' },
    { key: 'cost_name',          label: 'Cost Name',    width: 200 },
    { key: 'cost_amount',        label: 'Amount (Tk.)',   align: 'right', format: v => <strong>{fmt2(v)}</strong> },
    { key: 'remarks',            label: 'Remarks',      format: v => v || '—' },
    { key: 'created_by',         label: 'Created By',   format: v => <Typography variant="caption" color="text.secondary">{v}</Typography> },
  ];
 
  const summaryCards: SummaryCard[] = [
    { label: 'Records',          value: rows.length,                  color: COST_ACCENT },
    { label: 'Total Amount (Tk.)', value: fmt2(totals.total_amount ?? 0), color: '#FF9800' },
  ];
 
  const totalsRow = rows.length > 0 && !loading ? (
    <TableRow sx={{ background: alpha(COST_ACCENT, 0.06) }}>
      <TableCell colSpan={3} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>TOTALS</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800' }}>{fmt2(totals.total_amount ?? 0)}</TableCell>
      <TableCell colSpan={2} />
    </TableRow>
  ) : undefined;
 
  return (
    <Fade in timeout={400}><div>
      <ReportLayout title="Cost Information Report" subtitle="All cost/expense entries by date and category"
        accentColor={COST_ACCENT} columns={columns} rows={rows} loading={loading}
        tableMinWidth={1500}
        summaryCards={summaryCards} onRefresh={fetchData} onDownload={handleDownload}
        totalsRow={totalsRow} emptyIcon={<CostIcon sx={{ fontSize: 40 }} />}
        filters={
          <ReportFilterBar accentColor={COST_ACCENT} values={filters}
            onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
            onClear={() => setFilters({ date_from: '', date_to: '', cost_category_id: '' })}
            fields={[
              { type: 'date',   key: 'date_from',        label: 'From' },
              { type: 'date',   key: 'date_to',          label: 'To' },
              { type: 'select', key: 'cost_category_id', label: 'Category', options: costCats, minWidth: 180 },
            ]}
          />
        }
      />
    </div></Fade>
  );
};
export default CostInfoReport;